import { Form } from 'react-router'
import { z } from 'zod'
import { Icon } from '#app/components/ui/icon.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { useIsPending } from './misc.tsx'

export const GITHUB_PROVIDER_NAME = 'github'
// to add another provider, set their name here and add it to the providerNames below

export const providerNames = [GITHUB_PROVIDER_NAME] as const
export const ProviderNameSchema = z.enum(providerNames)
export type ProviderName = z.infer<typeof ProviderNameSchema>

/**
 * The providers offered on screen. None (fase 5b): the circle signs in
 * with e-mail and a passkey, and a GitHub button only raised the question
 * what GitHub is. Hidden, not closed: the /auth/:provider route still
 * signs in an existing connection and, like the e-mail form, can still
 * start a signup when posted to.
 */
export const shownProviderNames: readonly ProviderName[] = []

export const providerLabels: Record<ProviderName, string> = {
	[GITHUB_PROVIDER_NAME]: 'GitHub',
} as const

export const providerIcons: Record<ProviderName, React.ReactNode> = {
	[GITHUB_PROVIDER_NAME]: <Icon name="github-logo" />,
} as const

export function ProviderConnectionForm({
	redirectTo,
	type,
	providerName,
}: {
	redirectTo?: string | null
	type: 'Connect' | 'Login' | 'Signup'
	providerName: ProviderName
}) {
	const label = providerLabels[providerName]
	// Dutch button copy per flow (Sprint 4 review fix: these were the last
	// English strings on the translated auth screens).
	const typeLabelNl: Record<typeof type, string> = {
		Login: 'Inloggen met',
		Signup: 'Registreren met',
		Connect: 'Koppelen met',
	}
	const formAction = `/auth/${providerName}`
	const isPending = useIsPending({ formAction })
	return (
		<Form
			className="flex items-center justify-center gap-2"
			action={formAction}
			method="POST"
		>
			{redirectTo ? (
				<input type="hidden" name="redirectTo" value={redirectTo} />
			) : null}
			<StatusButton
				type="submit"
				variant="secondary"
				size="block"
				status={isPending ? 'pending' : 'idle'}
				pendingLabel={`Doorsturen naar ${label}…`}
			>
				<span className="inline-flex items-center gap-1.5">
					{providerIcons[providerName]}
					<span>
						{typeLabelNl[type]} {label}
					</span>
				</span>
			</StatusButton>
		</Form>
	)
}
